export default function FlowSection() {
  const steps = [
    {
      number: '01',
      title: '無料相談',
      description: 'まずはお気軽にご相談ください。物件の状況をヒアリングし、収益シミュレーションをご提示します。',
      icon: 'ri-chat-3-line',
    },
    {
      number: '02',
      title: '現地調査・プラン提案',
      description: '実際に物件を拝見し、最適な運営プランをご提案。初期費用や収益見込みを詳しくご説明します。',
      icon: 'ri-home-5-line',
    },
    {
      number: '03',
      title: 'ご契約・準備開始',
      description: 'プランにご納得いただけましたらご契約。リスティング作成・写真撮影・備品準備などを進めます。',
      icon: 'ri-file-text-line',
    },
    {
      number: '04',
      title: '運営開始',
      description: '予約受付開始。ゲスト対応・清掃手配・価格調整など、すべて弊社が代行いたします。',
      icon: 'ri-rocket-line',
    },
  ];

  return (
    <section className="py-24 relative overflow-hidden">
      {/* 和紙風背景 */}
      <div className="absolute inset-0 bg-gradient-to-br from-stone-50 via-amber-50 to-stone-50" />
      
      {/* 上部装飾ライン */}
      <div className="absolute top-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60" />
      <div className="absolute top-3 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      
      {/* 市松模様装飾 */}
      <div className="absolute top-8 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-40`}
          />
        ))}
      </div>

      <div className="relative z-10 max-w-6xl mx-auto px-6">
        {/* Header */}
        <div className="text-center mb-16">
          <h2 className="text-4xl sm:text-5xl font-bold text-slate-900 mb-4" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            <span className="text-[#8b2500]">◆</span> ご利用の流れ <span className="text-[#8b2500]">◆</span>
          </h2>
          <div className="flex items-center justify-center gap-2 mb-6">
            <div className="h-px w-16 bg-gradient-to-r from-transparent to-[#c9a84c]" />
            <span className="text-[#c9a84c] text-sm" style={{ fontFamily: "'Noto Serif JP', serif" }}>✦</span>
            <div className="h-px w-16 bg-gradient-to-l from-transparent to-[#c9a84c]" />
          </div>
          <p className="text-lg text-slate-600" style={{ fontFamily: "'Noto Serif JP', serif" }}>
            お問い合わせから運営開始まで、スムーズにサポートいたします
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* 接続ライン */}
          <div className="hidden md:block absolute top-20 left-0 right-0 h-1 border-t-2 border-dashed border-[#c9a84c]" style={{ left: '12.5%', right: '12.5%' }} />

          <div className="grid md:grid-cols-4 gap-8">
            {steps.map((step, idx) => (
              <div key={idx} className="relative">
                <div className="bg-white border-2 border-[#8b2500] rounded-2xl p-6 hover:shadow-xl transition-all hover:-translate-y-1 relative">
                  {/* 四隅装飾 */}
                  <div className="absolute top-0 left-0 w-4 h-4 border-t-2 border-l-2 border-[#c9a84c]" />
                  <div className="absolute top-0 right-0 w-4 h-4 border-t-2 border-r-2 border-[#c9a84c]" />
                  <div className="absolute bottom-0 left-0 w-4 h-4 border-b-2 border-l-2 border-[#c9a84c]" />
                  <div className="absolute bottom-0 right-0 w-4 h-4 border-b-2 border-r-2 border-[#c9a84c]" />

                  {/* Icon */}
                  <div className="relative mb-6">
                    <div className="w-16 h-16 mx-auto flex items-center justify-center bg-gradient-to-br from-[#8b2500] to-[#a52a00] rounded-full shadow-lg relative z-10">
                      <i className={`${step.icon} text-white text-2xl`}></i>
                    </div>
                    <div className="absolute top-0 left-1/2 -translate-x-1/2 -translate-y-3 text-5xl font-bold text-[#c9a84c] opacity-20" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                      {step.number}
                    </div>
                  </div>

                  {/* Content */}
                  <h3 className="text-xl font-bold text-slate-900 mb-3 text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    <span className="text-[#c9a84c] mr-1">◇</span>
                    {step.title}
                  </h3>
                  <p className="text-sm text-slate-600 leading-relaxed text-center" style={{ fontFamily: "'Noto Serif JP', serif" }}>
                    {step.description}
                  </p>
                </div>

                {/* Arrow (hidden on last item) */}
                {idx < steps.length - 1 && (
                  <div className="hidden md:flex absolute top-20 -right-4 items-center justify-center w-8 h-8 text-[#c9a84c] z-20">
                    <i className="ri-arrow-right-line text-2xl"></i>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="mt-16 text-center">
          <div className="inline-block bg-white border-2 border-[#8b2500] rounded-2xl p-8 shadow-lg relative">
            {/* 四隅装飾 */}
            <div className="absolute top-0 left-0 w-6 h-6 border-t-4 border-l-4 border-[#c9a84c]" />
            <div className="absolute top-0 right-0 w-6 h-6 border-t-4 border-r-4 border-[#c9a84c]" />
            <div className="absolute bottom-0 left-0 w-6 h-6 border-b-4 border-l-4 border-[#c9a84c]" />
            <div className="absolute bottom-0 right-0 w-6 h-6 border-b-4 border-r-4 border-[#c9a84c]" />

            <p className="text-lg text-slate-700 mb-6" style={{ fontFamily: "'Noto Serif JP', serif" }}>
              まずは無料相談から始めませんか？<br />
              お気軽にお問い合わせください。
            </p>
            <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent mb-4" />
            <a
              href="#contact"
              className="inline-flex items-center gap-2 bg-gradient-to-r from-[#8b2500] via-[#a52a00] to-[#8b2500] hover:from-[#a52a00] hover:via-[#8b2500] hover:to-[#a52a00] text-white font-bold px-10 py-4 rounded-lg transition-all hover:shadow-xl hover:-translate-y-0.5 whitespace-nowrap cursor-pointer shadow-lg"
              style={{ fontFamily: "'Noto Serif JP', serif" }}
            >
              <span className="text-[#c9a84c]">✦</span>
              無料相談を申し込む
              <i className="ri-arrow-right-line text-lg"></i>
            </a>
            <div className="h-px bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent mt-4" />
          </div>
        </div>
      </div>

      {/* 下部装飾ライン */}
      <div className="absolute bottom-0 left-0 right-0 flex justify-center gap-1">
        {[...Array(20)].map((_, i) => (
          <div
            key={i}
            className={`w-3 h-3 ${i % 2 === 0 ? 'bg-[#8b2500]' : 'bg-[#c9a84c]'} opacity-40`}
          />
        ))}
      </div>
      <div className="absolute bottom-4 left-0 right-0 h-1 bg-gradient-to-r from-transparent via-[#c9a84c] to-transparent" />
      <div className="absolute bottom-0 left-0 right-0 h-3 bg-gradient-to-r from-transparent via-[#8b2500] to-transparent opacity-60" />
    </section>
  );
}